import './header.scss';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ProfileIcon from 'assets/images/iPrise/ProfileIcon.svg';
import HeaderLogo from 'assets/images/RiseIt-black.svg';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  usePortalAccess,
  useUserProfile,
} from 'services/profile/profileService';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';

export interface NavItem {
  icon: string;
  label: string;
  path: string;
}

interface HeaderProps {
  navItems: NavItem[];
  profilePath: string;
  basePath?: string; // Used for checking active routes (e.g., '/management' or '/business-head')
}

const Header: React.FC<HeaderProps> = ({ navItems, profilePath, basePath }) => {
  const { profileStore } = useStore();
  const { isProfileInfo } = profileStore;
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Get user info from Redux store
  const { userInfo }: any = useAppSelector((store: any) => store.userLogin);

  // Fetch user profile and sync to MobX ProfileStore
  useUserProfile(userInfo?.id);

  // Fetch portal access using custom hook
  const { portals } = usePortalAccess();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  // Custom function to check if nav item is active
  const isNavActive = (path: string) => {
    // Special handling for teams route that includes team detail pages
    if (basePath === '/management' && path === '/management/teams') {
      return (
        location.pathname === '/management/teams' ||
        location.pathname.startsWith('/management/team/')
      );
    }
    // Special handling for job queue route that includes view-job pages
    if (path.endsWith('/job-queue')) {
      return (
        location.pathname === path || location.pathname.includes('/view-job/')
      );
    }
    return location.pathname === path;
  };

  const activeNav = navItems.find((item) => isNavActive(item.path));

  return (
    <Box className="management-header-root">
      <AppBar position="static" className="management-appbar" elevation={0}>
        <Toolbar className="management-toolbar">
          <Grid
            container
            alignItems="center"
            sx={{ flexWrap: 'nowrap', height: '100%' }}>
            <Grid item xs="auto">
              <img src={HeaderLogo} alt="RiseIT" className="header-logo" />
            </Grid>
            <Grid
              item
              xs
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'flex-start',
                ml: { md: 12, xs: 0 },
              }}>
              {isMobile ? (
                <>
                  <Box
                    sx={{
                      alignItems: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                    }}
                    onClick={handleMenuOpen}>
                    <IconButton size="small" sx={{ mr: 1 }}>
                      <MenuIcon />
                    </IconButton>
                    {activeNav && (
                      <Box
                        sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        <img
                          src={activeNav.icon}
                          alt=""
                          style={{ height: 14, width: 14 }}
                        />
                        <span
                          style={{
                            color: '#172b4d',
                            fontFamily: 'Inter',
                            fontSize: 14,
                            fontWeight: 600,
                          }}>
                          {activeNav.label}
                        </span>
                      </Box>
                    )}
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                      sx: {
                        border: '1px solid #dfe1e6',
                        borderRadius: '10px',
                        boxShadow: '0px 8px 16px rgba(0,0,0,0.1)',
                        minWidth: 180,
                        mt: 1.5,
                      },
                    }}>
                    {navItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => handleNavClick(item.path)}
                        selected={isNavActive(item.path)}
                        sx={{
                          '&.Mui-selected': {
                            '&:hover': { background: '#fff1f1' },
                            backgroundColor: '#fff9f9',
                            color: '#be0000',
                            fontFamily: 'Inter !important',
                            fontWeight: 600,
                          },
                          fontSize: 14,
                          gap: 1.5,
                          py: 1.5,
                        }}>
                        <img
                          src={item.icon}
                          alt=""
                          style={{
                            filter: isNavActive(item.path)
                              ? 'invert(19%) sepia(97%) saturate(4826%) hue-rotate(352deg) brightness(88%) contrast(94%)'
                              : 'none',
                            height: 16,
                            width: 16,
                          }}
                        />
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                <Box className="nav-container">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={() =>
                        isNavActive(item.path)
                          ? 'management-nav-link active'
                          : 'management-nav-link'
                      }>
                      <img
                        src={item.icon}
                        alt={item.label}
                        className="nav-icon"
                      />
                      {item.label}
                    </NavLink>
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs="auto">
              <Box className="actions-container">
                {!isMobile && portals.length > 0 && (
                  <Box className="header-button-group">
                    {portals.map((portal: any, index: number) => (
                      <Button
                        key={portal.id}
                        className={
                          portal.id === 2 ? 'portal-btn' : 'dashboard-btn'
                        }
                        disableRipple
                        component={portal.id === 1 ? 'a' : 'button'}
                        href={portal.id === 1 ? portal.url : undefined}
                        target={portal.id === 1 ? '_blank' : undefined}
                        rel={
                          portal.id === 1 ? 'noopener noreferrer' : undefined
                        }>
                        {portal.name}
                      </Button>
                    ))}
                  </Box>
                )}
                <Box
                  className="user-profile"
                  onClick={() => navigate(profilePath)}>
                  <Box className="profile-avatar">
                    {isProfileInfo?.imageId ? (
                      <img
                        src={`${process.env.REACT_APP_APIURL}${
                          MASTER_ENDPOINT.Files
                        }/${String(
                          isProfileInfo.profileImage?.id,
                        )}?userId=${String(userInfo?.id)}`}
                        alt="Profile"
                        className="profile-image"
                      />
                    ) : (
                      <img
                        src={ProfileIcon}
                        alt="Profile"
                        className="profile-icon"
                      />
                    )}
                  </Box>
                  <KeyboardArrowDownIcon className="profile-arrow" />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Box className="management-content-box">
        <Outlet />
      </Box>
    </Box>
  );
};

export default observer(Header);
